import React, { useState } from 'react';
import styled from 'styled-components';

import { IconChevronDown, IconInfo } from 'assets/icons';

import { useIsMobile } from 'hooks/common';
import { BookingConnection, FixedTicket } from 'types/api';

import SellingStatusSection from './SellingStatusSection';
import BookingDetailModalHeader from './BookingDetailModalHeader';
import SellingDetailSection from './SellingDetailSection';
import dayjs from 'dayjs';

const ModalLayout = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: row;

  position: relative;
  overflow: scroll;

  width: 100%;
  height: 100%;

  ${({ $isMobile }) => !$isMobile && 'min-height: 748px; '};

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;

  overflow: auto;

  height: auto;
`;

const TabBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  position: sticky;
  top: 0;
  left: 0;
  z-index: 16;

  min-width: 168px;
  height: 100%;

  padding: 32px 20px;

  button {
    height: 44px;
    padding: 0 16px;

    border: none;
    border-radius: 12px;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    text-align: left;
    background-color: transparent;
    color: rgba(70, 71, 76, 1);

    &.selected {
      background-color: rgba(244, 244, 245, 1);
      color: rgba(65, 75, 86, 1);
    }
  }
`;

const ModalContent = styled.div`
  width: 100%;
  height: 100%;
  overflow: scroll;

  @media (max-width: 768px) {
    padding-bottom: 69px;
  }
`;

const SeeAllDetailButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 16;

  padding: 24px 0;

  /* margin-top: 32px; */

  border-top: 1px solid;
  border-color: rgba(234, 235, 236, 1);

  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: rgba(152, 155, 162, 1);
  background-color: #fff;

  cursor: pointer;

  > svg {
    transform: rotate(-90deg);
  }
`;

const DividerBar = styled.div`
  width: calc(100% - 40px);
  height: 1px;
  min-height: 1px;

  margin: 0 auto 32px;
  padding: 0 20px;

  background-color: rgba(234, 235, 236, 1);
`;

type TabType = 'saleStatus' | 'allHistory';

interface Props {
  item: FixedTicket;
  handleModal: () => void;
  bookingItems: BookingConnection;
  onSearch: () => void;
  keywordHandler: (value: string) => void;
  searchKeyword: string;
  searchedKeyword: string;
  handleRefetchBookingList: () => void;
  bookingItemsLoading: boolean;
  handleRefetchBookingItemList: () => void;
  handleCancelBookingConfirmModal: () => void;
  saveSelectedConfirmedBookingIds: (
    selectedConfirmedBookingIds: string[],
  ) => void;
}

/* 지정형 모달 */
function FixedBookingDetailContent({
  item,
  handleModal,
  bookingItems,
  onSearch,
  keywordHandler,
  searchKeyword,
  searchedKeyword,
  handleRefetchBookingList,
  bookingItemsLoading,
  handleRefetchBookingItemList,
  handleCancelBookingConfirmModal,
  saveSelectedConfirmedBookingIds,
}: Props) {
  const [selectedTab, setSelectedTab] = useState<TabType>('saleStatus');
  const isMobile = useIsMobile();

  const handleTabClick = (tab: 'saleStatus' | 'allHistory') => {
    setSelectedTab(tab);
  };

  const startTime = item?.term.startedAt;
  const endTime = item?.term.endedAt;

  const actionTime = new Date(item.saleTerm.endedAt); // 신청 마감일
  const isOverActionTime = dayjs().isAfter(dayjs(actionTime));

  return (
    bookingItems && (
      <ModalLayout $isMobile={isMobile}>
        {isOverActionTime ? (
          <></>
        ) : (
          !isMobile && (
            <TabBox>
              <button
                className={selectedTab === 'saleStatus' ? 'selected' : ''}
                onClick={() => handleTabClick('saleStatus')}
              >
                판매현황
              </button>

              <button
                className={selectedTab === 'allHistory' ? 'selected' : ''}
                onClick={() => handleTabClick('allHistory')}
              >
                내역 전체보기
              </button>
            </TabBox>
          )
        )}

        <ModalContent>
          <BookingDetailModalHeader
            isMobile={isMobile}
            startTime={startTime}
            endTime={endTime}
            item={item}
            handleModal={handleModal}
            handleRefetchBookingList={handleRefetchBookingList}
            ticketType={'fixed'}
            isOverActionTime={isOverActionTime}
          />

          <ModalBody>
            {/* 판매중 내역 */}
            {isOverActionTime ? (
              <></>
            ) : isMobile ? (
              <SellingStatusSection item={item} />
            ) : (
              selectedTab === 'saleStatus' && (
                <SellingStatusSection item={item} />
              )
            )}

            {isMobile && selectedTab === 'allHistory' && <DividerBar />}

            {(selectedTab === 'allHistory' || isOverActionTime) && (
              <SellingDetailSection
                bookingItems={bookingItems}
                searchedKeyword={searchedKeyword}
                searchKeyword={searchKeyword}
                keywordHandler={keywordHandler}
                onSearch={onSearch}
                bookingItemsLoading={bookingItemsLoading}
                handleRefetchBookingItemList={handleRefetchBookingItemList}
                ticketType={isOverActionTime ? 'unfixed' : 'fixed'}
                startTime={startTime}
                handleCancelBookingConfirmModal={
                  handleCancelBookingConfirmModal
                }
                saveSelectedConfirmedBookingIds={
                  saveSelectedConfirmedBookingIds
                }
                ticketOriginType={'fixed'}
              />
            )}

            {isOverActionTime ? (
              <></>
            ) : (
              isMobile && (
                <SeeAllDetailButton
                  onClick={() =>
                    handleTabClick(
                      selectedTab === 'saleStatus'
                        ? 'allHistory'
                        : 'saleStatus',
                    )
                  }
                >
                  {selectedTab === 'saleStatus' ? '내역 전체보기' : '내역 접기'}
                  <IconChevronDown
                    width={16}
                    height={16}
                    style={{
                      transform:
                        selectedTab === 'saleStatus'
                          ? 'rotate(-90deg)'
                          : 'rotate(-180deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </SeeAllDetailButton>
              )
            )}
          </ModalBody>
        </ModalContent>
      </ModalLayout>
    )
  );
}

export default FixedBookingDetailContent;
